import { Box, CircularProgress, InputBase, Tooltip, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

/** The server's limit on folder and project names. */
const MAX_NAME = 80;

/**
 * Focus the field with its text selected, a frame later: a menu that was just
 * clicked hands focus back to its button as it closes, which would otherwise
 * blur the field straight away. Returns the cancel, for an effect cleanup.
 */
const focusAndSelect = (ref: React.RefObject<HTMLInputElement | null>) => {
  const frame = requestAnimationFrame(() => {
    ref.current?.focus();
    ref.current?.select();
  });
  return () => cancelAnimationFrame(frame);
};

export interface InlineNameEdit {
  /** Saving. */
  busy: boolean;
  /** The server's explanation when it refused (duplicate name, too deep...). */
  error: string | null;
  /** The trimmed name; a blank field sends the name it started with. */
  onCommit: (name: string) => void;
  onCancel: () => void;
}

interface InlineNameFieldProps extends InlineNameEdit {
  /** Pre-filled and selected, so typing replaces it. */
  initialName: string;
  /** What the field names, for screen readers ("Folder name"). */
  ariaLabel: string;
  /** Border and spinner colour: amber for folders, teal for projects. */
  accent?: string;
  /** The caption while typing, under the field. */
  hint?: string;
  /**
   * Where the hint and errors go: a caption line under the field (table
   * rows), or a tooltip under it (the breadcrumb, which has no room).
   */
  messages: "caption" | "tooltip";
  sx?: SxProps<Theme>;
}

/**
 * A name being typed in place, like naming a folder in a file manager.
 * Enter or clicking away saves; Escape gives up.
 */
export default function InlineNameField({
  initialName,
  ariaLabel,
  accent = "#f59e0b",
  hint = "Press Enter to save or Esc to cancel",
  busy,
  error,
  onCommit,
  onCancel,
  messages,
  sx,
}: InlineNameFieldProps) {
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Set once a name is sent, so the blur that follows (or the field unmounting
  // on success) doesn't send it again. Cleared when the server refuses.
  const sentRef = useRef(false);
  const refusedNameRef = useRef<string | null>(null);

  useEffect(() => focusAndSelect(inputRef), []);
  useEffect(() => {
    if (!error) return;
    sentRef.current = false;
    return focusAndSelect(inputRef);
  }, [error]);

  const commit = () => {
    if (sentRef.current || busy) return;
    const trimmed = name.trim() || initialName;
    sentRef.current = true;
    refusedNameRef.current = trimmed;
    onCommit(trimmed);
  };

  const field = (
    <InputBase
      inputRef={inputRef}
      value={name}
      readOnly={busy}
      onChange={(e) => setName(e.target.value)}
      // The row or crumb around the field opens the folder on click.
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          if (!busy) onCancel();
        }
      }}
      onBlur={() => {
        // Switching to another window or tab isn't finishing the name; the
        // field gets its focus back on return.
        if (!document.hasFocus()) return;
        // Walking away from a name the server just refused gives up rather
        // than sending the same name again.
        if (error && name.trim() === refusedNameRef.current) onCancel();
        else commit();
      }}
      inputProps={{ maxLength: MAX_NAME, "aria-label": ariaLabel }}
      endAdornment={busy ? <CircularProgress size={14} sx={{ ml: 1, color: accent }} /> : null}
      sx={[
        {
          fontWeight: 600,
          width: "100%",
          maxWidth: 360,
          px: 0.75,
          ml: -0.75,
          borderRadius: 1,
          backgroundColor: "#fff",
          border: 1,
          borderColor: error ? "error.main" : accent,
          "& .MuiInputBase-input": { py: 0.25 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );

  if (messages === "tooltip") {
    return (
      <Tooltip open={!!error} title={error ?? ""} placement="bottom-start" arrow describeChild>
        {field}
      </Tooltip>
    );
  }
  return (
    <Box sx={{ minWidth: 0 }}>
      {field}
      <Typography variant="caption" color={error ? "error" : "text.secondary"} sx={{ display: "block" }}>
        {error ?? (busy ? "Saving…" : hint)}
      </Typography>
    </Box>
  );
}
